import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { orders as ordersApi, suppliers as sApi, agents as agentsApi, quotations as qApi, samples as samplesApi, tracking as trackingApi } from '../utils/api';
import { getUser } from '../utils/auth';
import { ORDER_STATUS_LIST, ORDER_STATUS_META, ORDER_STATUS_PT, fmtBRL, fmtUSD, fmtDate } from '../utils/helpers';
import OrderTab from '../components/OrderTab';
import PaymentsTab from '../components/PaymentsTab';
import ShipmentTab from '../components/ShipmentTab';
import DownloadsPanel from '../components/DownloadsPanel';
import OrderHistory from '../components/OrderHistory';
import PrintReport from '../components/PrintReport';
import { useToast, ToastContainer } from '../hooks/useToast';
import { useT } from '../i18n/I18nContext';
import { fobStatus, FOB_META, fobShort } from '../utils/fob';

const O_PAGE_LIMIT = 20;

function ConfirmModal({ message, onConfirm, onCancel, cancelLabel, confirmLabel }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }}>
      <div style={{
        background:'white', borderRadius:12, padding:28, maxWidth:400, width:'100%',
        boxShadow:'0 20px 60px rgba(0,0,0,.3)',
      }}>
        <div style={{ fontSize:24, marginBottom:12, textAlign:'center' }}>⚠️</div>
        <p style={{ textAlign:'center', fontSize:15, color:'var(--gray-700)', margin:'0 0 24px', lineHeight:1.5 }}>
          {message}
        </p>
        <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
          <button onClick={onCancel}
            style={{ padding:'9px 22px', border:'1px solid var(--gray-200)', borderRadius:8, background:'white', cursor:'pointer', fontSize:14 }}>
            {cancelLabel}
          </button>
          <button onClick={onConfirm}
            style={{ padding:'9px 22px', background:'var(--pantone)', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Orders() {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [ordersList, setOrdersList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [fobFilter, setFobFilter] = useState(false);   // só pedidos com FOB a cobrar
  const [typeFilter, setTypeFilter] = useState(null);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const { toasts, toast } = useToast();

  const loadOrders = useCallback(async (pageOverride) => {
    const p = pageOverride !== undefined ? pageOverride : page;
    const data = await ordersApi.list({ page: p, limit: O_PAGE_LIMIT });
    setOrdersList(data.rows || []);
    setTotal(data.total || 0);
    setPage(data.page || 1);
    setPages(data.pages || 1);
  }, []); // eslint-disable-line

  useEffect(() => { loadOrders(); }, [loadOrders]);

  useEffect(() => {
    if (id) qApi.get(id).then(q => {
      // Consistência de tela: um registro que ainda é cotação abre na tela de cotação
      if (!q.order_type || q.order_type === 'quotation') { navigate(`/quotations/${id}`, { replace: true }); return; }
      setSelected(q);
    }).catch(() => setSelected(null));
    else setSelected(null);
  }, [id, navigate]);

  const fobToChase = o => ['late', 'due_soon'].includes(fobStatus(o).status);
  const fobCount = ordersList.filter(fobToChase).length;
  const filtered = ordersList.filter(o => {
    if (fobFilter && !fobToChase(o)) return false;
    if (statusFilter && o.status !== statusFilter) return false;
    if (typeFilter && o.order_type !== typeFilter) return false;
    if (filter) {
      const s = filter.toLowerCase();
      return (o.reference||'').toLowerCase().includes(s)
        || (o.supplier_name||'').toLowerCase().includes(s)
        || (o.collection||'').toLowerCase().includes(s);
    }
    return true;
  });

  if (selected) {
    const fromSamples = searchParams.get('from') === 'samples';
    return <OrderDetail
      order={selected}
      backLabel={fromSamples ? t('nav.samples') : null}
      onBack={() => fromSamples ? navigate('/samples') : navigate('/orders')}
      onRefresh={async () => { const fresh = await qApi.get(selected.id); setSelected(fresh); }}
      toast={toast}
      toasts={toasts}
    />;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">📦 {t('orders.title')}</div>
          <div className="page-subtitle">
            {total} {t('orders.title').toLowerCase()} {pages > 1 ? ` · ${t('pagination.of')} ${pages}` : ''}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder={t('search.order')}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ maxWidth: 460 }}
          />
          {(filter || statusFilter || typeFilter || fobFilter) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilter(''); setStatusFilter(null); setTypeFilter(null); setFobFilter(false); }}>{t('filter.clear')}</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className={`status-chip${!typeFilter ? ' status-chip--active' : ''}`} onClick={() => setTypeFilter(null)}>{t('filter.all_types')}</button>
          {['sample','bulk'].map(tp => (
            <button key={tp} className={`status-chip${typeFilter === tp ? ' status-chip--active' : ''}`}
              onClick={() => setTypeFilter(typeFilter === tp ? null : tp)}>
              {tp === 'sample' ? '🟠 Sample' : '🔵 Bulk'}
            </button>
          ))}
          <span style={{ margin: '0 4px', color: 'var(--gray-300)' }}>|</span>
          <button className={`status-chip${fobFilter ? ' status-chip--active' : ''}`} onClick={() => setFobFilter(f => !f)}
            title="Data FOB atrasada ou vencendo em até 7 dias" data-testid="fob-filter">
            🏭 FOB a cobrar
            <span style={{ marginLeft: 5, padding: '1px 6px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: fobFilter ? 'rgba(255,255,255,.25)' : (fobCount ? '#FECACA' : 'var(--gray-200)'),
              color: fobFilter ? 'white' : (fobCount ? '#B91C1C' : 'var(--gray-600)') }}>{fobCount}</span>
          </button>
          <span style={{ margin: '0 4px', color: 'var(--gray-300)' }}>|</span>
          <button className={`status-chip${!statusFilter ? ' status-chip--active' : ''}`} onClick={() => setStatusFilter(null)}>{t('filter.all_status')}</button>
          {ORDER_STATUS_LIST.map(s => {
            const count = ordersList.filter(o => o.status === s).length;
            if (count === 0) return null;
            return (
              <button key={s}
                className={`status-chip${statusFilter === s ? ' status-chip--active' : ''}`}
                onClick={() => setStatusFilter(statusFilter === s ? null : s)}>
                {ORDER_STATUS_META[s]?.emoji} {ORDER_STATUS_PT[s] || s}
                <span style={{ marginLeft: 5, padding: '1px 6px', borderRadius: 999, fontSize: 11,
                  background: statusFilter === s ? 'rgba(255,255,255,.25)' : 'var(--gray-200)',
                  color: statusFilter === s ? 'white' : 'var(--gray-600)', fontWeight: 700 }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <div className="empty-title">{t('orders.empty')}</div>
            <div className="empty-sub">{t('orders.empty.sub')}</div>
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th>{t('col.reference')}</th>
                  <th>{t('col.type')}</th>
                  <th>{t('col.supplier')}</th>
                  <th>{t('col.collection')}</th>
                  <th>{t('col.agent')}</th>
                  <th>{t('col.items')}</th>
                  <th>{t('col.fob_total')}</th>
                  <th>{t('col.exchange')}</th>
                  <th>{t('col.status')}</th>
                  <th title="Data em que o fornecedor deve ter o pedido pronto FOB">🏭 FOB</th>
                  <th>{t('col.confirmed_at')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/orders/${o.id}`)}>
                    <td className="mono">{o.reference}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: o.order_type === 'sample' ? '#FFF3E0' : '#E3F2FD',
                        color: o.order_type === 'sample' ? '#E65100' : '#1565C0',
                      }}>
                        {o.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}
                      </span>
                    </td>
                    <td className="bold">{o.supplier_name}</td>
                    <td><span className="tag">{o.collection || '—'}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{o.agent_name || '—'}</td>
                    <td>{o.item_count}</td>
                    <td className="text-green">{o.negotiated_price ? fmtUSD(o.negotiated_price) : '—'}</td>
                    <td className="mono">{o.locked_exchange_rate ? `R$ ${Number(o.locked_exchange_rate).toFixed(4)}` : '—'}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: 'var(--gray-100)', color: 'var(--gray-600)',
                      }}>
                        {ORDER_STATUS_META[o.status]?.emoji} {ORDER_STATUS_PT[o.status] || o.status}
                      </span>
                    </td>
                    <td data-testid="fob-cell">
                      {(() => {
                        const f = fobStatus(o);
                        if (f.status === 'none') return <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>;
                        const m = FOB_META[f.status];
                        return (
                          <div title={m.label}>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{fmtDate(f.actual || f.date)}</div>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
                              {m.icon} {fobShort(f)}
                            </span>
                            {f.slip_days > 0 && <div style={{ fontSize: 10, color: '#B45309' }}>adiado +{f.slip_days}d</div>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-muted">{fmtDate(o.order_locked_at || o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { const p = page - 1; setPage(p); loadOrders(p); }} disabled={page <= 1}>{t('pagination.prev')}</button>
          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{page} {t('pagination.of')} {pages}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { const p = page + 1; setPage(p); loadOrders(p); }} disabled={page >= pages}>{t('pagination.next')}</button>
        </div>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}

const ALERT_META = {
  customs_hold:   { bg: '#FFF3E0', color: '#E65100', icon: '📦' },
  customs_action: { bg: '#FFF9C4', color: '#F57F17', icon: '⚠️' },
  payment_due:    { bg: '#FCE4EC', color: '#AD1457', icon: '💰' },
};

function TrackingCard({ trackingCache, onRefresh, refreshing, t }) {
  const [expanded, setExpanded] = React.useState(false);
  const events = (() => {
    if (!trackingCache?.events) return [];
    try { return typeof trackingCache.events === 'string' ? JSON.parse(trackingCache.events) : trackingCache.events; }
    catch { return []; }
  })();
  const alertMeta = trackingCache?.alert_type ? ALERT_META[trackingCache.alert_type] : null;

  return (
    <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)' }}>📡 {t('tracking.live_status')}</span>
        {trackingCache ? (
          <>
            {trackingCache.carrier && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)',
                background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 8 }}>
                {trackingCache.carrier}
              </span>
            )}
            {trackingCache.status_text && (
              <span style={{
                padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                background: alertMeta ? alertMeta.bg : '#EBF8FF',
                color: alertMeta ? alertMeta.color : '#2B6CB0',
              }}>
                {trackingCache.status_text}
              </span>
            )}
            {trackingCache.estimated_delivery && (
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                {t('tracking.edd')}: <strong>{trackingCache.estimated_delivery.slice(0, 10)}</strong>
              </span>
            )}
            {!trackingCache.status_text && trackingCache.tracking_number && (
              <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                {trackingCache.tracking_number}
              </span>
            )}
            {events.length > 0 && (
              <button onClick={() => setExpanded(v => !v)}
                style={{ fontSize: 12, color: 'var(--pantone)', background: 'none', border: 'none',
                  cursor: 'pointer', padding: '2px 6px', fontWeight: 600 }}>
                {expanded ? '▲' : '▼'} {events.length} eventos
              </button>
            )}
            {trackingCache.tracking_url && (
              <a href={trackingCache.tracking_url} target="_blank" rel="noreferrer"
                className="btn btn-ghost btn-sm" style={{ textDecoration: 'none', flexShrink: 0 }}>
                🔗 {t('tracking.open_carrier')}
              </a>
            )}
            {trackingCache.last_refreshed && (
              <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 'auto' }}>
                {trackingCache.last_refreshed.slice(0, 16).replace('T', ' ')}
              </span>
            )}
          </>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t('tracking.no_tracking')}</span>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onRefresh} disabled={refreshing}
          style={{ marginLeft: trackingCache ? 0 : 'auto', flexShrink: 0 }}>
          {refreshing ? t('tracking.refreshing') : t('tracking.refresh')}
        </button>
      </div>

      {/* Alert banner */}
      {alertMeta && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 'var(--radius)',
          background: alertMeta.bg, color: alertMeta.color, border: `1.5px solid ${alertMeta.color}44`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>{alertMeta.icon}</span>
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {t(`tracking.alert.${trackingCache.alert_type}`)}
            {trackingCache.alert_message ? ` — ${trackingCache.alert_message}` : ''}
          </span>
          {trackingCache.alert_link && (
            <a href={trackingCache.alert_link} target="_blank" rel="noreferrer"
              style={{ marginLeft: 'auto', fontSize: 12, color: alertMeta.color, fontWeight: 700, textDecoration: 'none' }}>
              {t('tracking.alert.action_link')}
            </a>
          )}
        </div>
      )}

      {/* Events history */}
      {expanded && events.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--gray-100)', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase',
            letterSpacing: 1, marginBottom: 8 }}>
            {t('tracking.events_title')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {events.map((ev, i) => {
              const time  = ev.checkpoint_time || ev.a || ev.time || '';
              const msg   = ev.message || ev.z || ev.desc || ev.standerdDesc || '';
              const city  = ev.city || ev.l || ev.cityName || '';
              const country = ev.country_name || '';
              const loc   = [city, country].filter(Boolean).join(', ');
              return (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '140px 1fr',
                  gap: '4px 16px', padding: '8px 0',
                  borderBottom: i < events.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', paddingTop: 2 }}>
                    {time ? new Date(time).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: i === 0 ? 'var(--pantone)' : 'var(--gray-700)',
                      fontWeight: i === 0 ? 600 : 400 }}>
                      {msg || '—'}
                    </div>
                    {loc && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{loc}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order: q, onBack, backLabel, onRefresh, toast, toasts }) {
  const t = useT();
  const navigate = useNavigate();
  const user = getUser();
  const [activeTab, setActiveTab] = useState('order');
  const [sampleData, setSampleData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [trackingCache, setTrackingCache] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Muda a cada ação que gera evento, para o histórico recarregar
  const [historyKey, setHistoryKey] = useState(0);

  // Toda ação das abas recarrega o pedido E o histórico — senão o card ficava
  // desatualizado ao mexer em outra aba (ele fica fora do sistema de abas).
  const refreshAll = React.useCallback(async () => {
    await onRefresh();
    setHistoryKey(k => k + 1);
  }, [onRefresh]);

  useEffect(() => {
    if (q.order_type === 'sample') {
      samplesApi.getTracking(q.id).then(setSampleData).catch(() => {});
    }
  }, [q.id, q.order_type]);

  useEffect(() => {
    trackingApi.get(q.id).then(data => setTrackingCache(data)).catch(() => {});
  }, [q.id]);

  async function handleRefreshTracking() {
    setRefreshing(true);
    try {
      const tn = sampleData?.tracking?.tracking_number || q.shipment?.tracking_number;
      const carrier = sampleData?.tracking?.carrier || q.shipment?.carrier;
      const body = tn ? { tracking_number: tn, carrier } : {};
      const data = await trackingApi.refresh(q.id, body);
      setTrackingCache(data);
      toast(t('tracking.refreshed'), 'success');
    } catch (err) {
      const errCode = err.response?.data?.error;
      if (errCode === 'no_tracking_number') toast(t('tracking.no_tracking'), 'error');
      else if (errCode === 'tracking_api_not_configured') toast(t('tracking.no_key'), 'warning');
      else toast(t('tracking.refresh_error'), 'error');
    } finally {
      setRefreshing(false);
    }
  }

  async function saveSampleTracking() {
    try {
      await samplesApi.saveTracking(q.id, sampleData);
      toast(t('orders.results_saved'), 'success');
    } catch (err) {
      toast(t('orders.results_error'), 'error');
    }
  }

  const tabs = [
    { key: 'order',    label: t('orders.tab.order') },
    { key: 'payments', label: t('orders.tab.payments') },
    { key: 'shipment', label: t('orders.tab.shipment') },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>← {backLabel || t('orders.title')}</button>
          <div>
            <div className="page-title" style={{ fontFamily: 'var(--font-mono)', fontSize: 18 }}>{q.reference}</div>
            <div className="page-subtitle">
              {q.supplier_name} · {q.collection || t('orders.no_collection')}
              <span style={{
                marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                background: q.order_type === 'sample' ? '#FFF3E0' : '#E3F2FD',
                color: q.order_type === 'sample' ? '#E65100' : '#1565C0',
              }}>
                {q.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}
              </span>
            </div>
          </div>
        </div>
        <div className="action-group">
          <span style={{ fontSize: 12, color: 'var(--gray-400)', padding: '8px 12px', background: 'var(--gray-100)', borderRadius: 'var(--radius)' }}>
            {ORDER_STATUS_META[q.status]?.emoji} {ORDER_STATUS_PT[q.status] || q.status}
          </span>
          <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>{t('print.btn')}</button>
        </div>
      </div>

      {user?.role === 'admin' && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
          <button
            onClick={() => setConfirm({
              message: `${t('orders.change_type_btn')} "${q.order_type === 'sample' ? 'Bulk' : 'Sample'}"?`,
              action: async () => {
                const newType = q.order_type === 'sample' ? 'bulk' : 'sample';
                await ordersApi.changeOrderType(q.id, newType);
                onRefresh();
              }
            })}
            style={{ padding:'6px 14px', fontSize:12, border:'1px solid var(--gray-200)', borderRadius:8, cursor:'pointer', background:'white' }}>
            {t('orders.change_type_btn')} {q.order_type === 'sample' ? 'Bulk' : 'Sample'}
          </button>
          <button
            onClick={() => setConfirm({
              message: `${t('orders.revert_btn').replace('↩ ','')}: "${q.reference}"?`,
              action: async () => {
                try {
                  await ordersApi.revertToQuotation(q.id);
                  navigate('/quotations/' + q.id);
                } catch(e) {
                  toast(e.response?.data?.error || t('common.error_saving'), 'error');
                }
              }
            })}
            style={{ padding:'6px 14px', fontSize:12, border:'1px solid #FECACA', borderRadius:8, cursor:'pointer', background:'#FFF5F5', color:'#C53030' }}>
            {t('orders.revert_btn')}
          </button>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          message={confirm.message}
          cancelLabel={t('orders.confirm.cancel')}
          confirmLabel={t('orders.confirm.confirm')}
          onConfirm={async () => { await confirm.action(); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}

      <TrackingCard
        trackingCache={trackingCache}
        onRefresh={handleRefreshTracking}
        refreshing={refreshing}
        t={t}
      />

      <DownloadsPanel quotation={q} toast={toast} onUploaded={() => setHistoryKey(k => k + 1)} />

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-100)', marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 400,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab.key ? '2px solid var(--pantone)' : '2px solid transparent',
              color: activeTab === tab.key ? 'var(--pantone)' : 'var(--gray-500)',
              marginBottom: -2,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'order' && <OrderTab quotation={q} onRefresh={refreshAll} />}
      {activeTab === 'payments' && <PaymentsTab quotation={q} onRefresh={refreshAll} toast={toast} />}
      {activeTab === 'shipment' && <ShipmentTab quotation={q} onRefresh={refreshAll} />}

      {q.order_type === 'sample' && sampleData && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:'var(--pantone)' }}>
            {t('orders.sample_results')}
          </h3>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:12, marginBottom:24 }}>
            {[
              { label: t('samples.col.tracking'),   field:'tracking_number', placeholder:'Ex: SF1234567890CN' },
              { label: t('quotations.sample.carrier'), field:'carrier', placeholder:'Ex: DHL, FedEx, SF Express' },
              { label: t('quotations.sample.dispatch_date'), field:'dispatched_at', type:'date' },
              { label: t('quotations.sample.received_date'), field:'received_at', type:'date' },
            ].map(f => (
              <div key={f.field}>
                <label style={{ fontSize:12, fontWeight:600, color:'var(--gray-600)', display:'block', marginBottom:4 }}>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  value={sampleData.tracking?.[f.field] || ''}
                  placeholder={f.placeholder}
                  onChange={e => setSampleData(prev => ({ ...prev, tracking: { ...prev.tracking, [f.field]: e.target.value } }))}
                  style={{ width:'100%', padding:'8px 10px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius)', fontSize:13 }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'var(--gray-600)', display:'block', marginBottom:4 }}>{t('orders.tracking.general_notes')}</label>
            <textarea
              rows={2}
              value={sampleData.tracking?.notes || ''}
              placeholder={t('quotations.sample.notes_placeholder')}
              onChange={e => setSampleData(prev => ({ ...prev, tracking: { ...prev.tracking, notes: e.target.value } }))}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius)', fontSize:13, resize:'vertical' }}
            />
          </div>

          {sampleData.items && sampleData.items.length > 0 && (
            <>
              <h4 style={{ fontSize:14, fontWeight:600, color:'var(--gray-700)', margin:'0 0 12px' }}>{t('orders.result_per_item')}</h4>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {sampleData.items.map((item, idx) => (
                  <div key={item.id} style={{ background:'var(--gray-50)', borderRadius:'var(--radius)', padding:16, display:'grid', gridTemplateColumns:'1fr auto auto', gap:12, alignItems:'start' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14 }}>{item.name}</div>
                      {item.description && <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>{item.description}</div>}
                      <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:4 }}>{t('orders.item.qty_ordered')}{item.qty}</div>
                      <textarea
                        rows={1}
                        placeholder={t('quotations.sample.obs_placeholder')}
                        value={item.sample_notes || ''}
                        onChange={e => setSampleData(prev => ({
                          ...prev,
                          items: prev.items.map((it, i) => i === idx ? { ...it, sample_notes: e.target.value } : it)
                        }))}
                        style={{ marginTop:8, width:'100%', padding:'6px 8px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius)', fontSize:12, resize:'none' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', display:'block', marginBottom:4 }}>{t('orders.item.qty_received')}</label>
                      <input
                        type="number"
                        min="0"
                        value={item.qty_sampled || 1}
                        onChange={e => setSampleData(prev => ({
                          ...prev,
                          items: prev.items.map((it, i) => i === idx ? { ...it, qty_sampled: parseInt(e.target.value) || 1 } : it)
                        }))}
                        style={{ width:80, padding:'6px 8px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius)', fontSize:13, textAlign:'center' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', display:'block', marginBottom:4 }}>{t('col.status')}</label>
                      <select
                        value={item.sample_status || 'pending'}
                        onChange={e => setSampleData(prev => ({
                          ...prev,
                          items: prev.items.map((it, i) => i === idx ? { ...it, sample_status: e.target.value } : it)
                        }))}
                        style={{ padding:'6px 8px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius)', fontSize:13, background:'white' }}
                      >
                        <option value="pending">⏳ Aguardando</option>
                        <option value="approved">✅ Aprovado</option>
                        <option value="rejected">❌ Rejeitado</option>
                        <option value="needs_revision">🔄 Revisar</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ marginTop:20, display:'flex', justifyContent:'flex-end' }}>
            <button className="btn btn-primary" onClick={saveSampleTracking}>
              {t('orders.save_results')}
            </button>
          </div>
        </div>
      )}

      <OrderHistory quotation={q} refreshKey={historyKey} />

      <PrintReport quotation={q} />

      <ToastContainer toasts={toasts} />
    </div>
  );
}
